import React, { useEffect, useState, useRef } from 'react';
import VerticalScroller, { ScrollerOption } from '../../components/molecules/VerticalScroller/VerticalScroller';
import { useTranslation } from 'react-i18next';
import { Typography } from '../../components/atoms';
import { ADVISOR_ROUTES } from '../../routes/routeConstants';
import { useNavigate } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more';
import { createDoughnutOptions } from '../../common/chartConfigs';
import { useCMSContent } from '../../common/hooks/useCmsContent';
import { useTheme } from '@mui/material/styles';

highchartsMore(Highcharts);

export type MinMaxEquityItem = {
    riskProfile: string;
    minEquity: number;
    maxEquity: number;
    portfolioCount: number;
};
interface RiskToleranceProps {
    clientName: string;
    riskOptions: ScrollerOption[];
    selectedRiskProfile: string;
    // eslint-disable-next-line no-unused-vars
    updateRisk?: (selectedRiskProfile: string) => void;
    adviceClientRiskProfile?: string;
    updateAdviceClientRiskProfile?: () => void;
    minMaxEquity?: Record<string, MinMaxEquityItem>;
}

const RiskTolerance: React.FC<RiskToleranceProps> = ({
    clientName,
    riskOptions,
    selectedRiskProfile,
    updateRisk,
    adviceClientRiskProfile,
    updateAdviceClientRiskProfile,
    minMaxEquity = {}
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const cmsUtil = useCMSContent();
    const theme = useTheme();
    // Initialize selected risk with current client's risk profile or default to moderate
    const [selectedRisk, setSelectedRisk] = useState<string>(selectedRiskProfile);
    const chartRef = useRef<HighchartsReactRefObject>(null);

    const handleRiskChange = (selectedOption: string) => {
        if (selectedRisk === selectedOption) {
            return;
        }
        setSelectedRisk(selectedOption);
        if (updateRisk) updateRisk(selectedOption);
    };

    const dynamicLabel = `${t('SELECTED_FOR')} ${clientName}`;

    useEffect(() => {
        if (!adviceClientRiskProfile && updateAdviceClientRiskProfile) updateAdviceClientRiskProfile();
        if (location.pathname !== ADVISOR_ROUTES.RISK_TOLERANCE)
            navigate(ADVISOR_ROUTES.RISK_TOLERANCE, { replace: true });
    }, []);
    const chartOptions = createDoughnutOptions(selectedRisk, t, minMaxEquity, theme);

    return (
        <div className={`risk-tolerance-container ${riskOptions.length > 3 ? 'new' : ''}`}>
            <Typography
                label={dynamicLabel}
                color="semantic.text.default"
                variant="body.largeBold"
                sx={{ marginBottom: riskOptions.length > 3 ? '8px' : '30px', fontSize: 14, lineHeight: '19px' }}
            />
            <Typography
                label={cmsUtil.getContentAsString('risk_tolerance.content.disclaimer1')}
                color="semantic.text.muted"
                variant="body.large"
                sx={{ marginBottom: riskOptions.length > 3 ? '0px' : '60px'}}
            />
            {riskOptions.length > 0 && (
                <div className={`risk-tolerance-content ${riskOptions.length > 3 ? 'new' : ''}`}>
                    <div className="risk-tolerance-chart">
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                    </div>

                    <div className="risk-tolerance-scroller">
                        <VerticalScroller
                            options={riskOptions}
                            selectedId={selectedRisk}
                            onSelectionChange={handleRiskChange}
                            trackHeight={riskOptions.length > 3 ? 344 : 250}
                            indicatorSize={45}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default RiskTolerance;
